import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


// Create the user context
const UserContext = createContext();

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Simple function to extract userId from JWT token
  const extractUserIdFromToken = (token) => {
    if (!token) return null;
    
    try {
      // Parse the token
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      return decoded.userId || null;
    } catch (error) {
      logger.error('Error extracting userId from token:', error);
      return null;
    }
  };

  // Check for token on mount and set authentication state
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        // Extract userId from token and ensure it's in localStorage
        const tokenUserId = extractUserIdFromToken(token);
        if (tokenUserId) {
          const storedUserId = localStorage.getItem('userId');
          if (!storedUserId || storedUserId !== tokenUserId) {
            logger.debug('🔍 Fixing userId in localStorage from token on mount:', tokenUserId);
            localStorage.setItem('userId', tokenUserId);
          }
        }
        
        logger.debug('🔍 Token found on mount, validating...');
        try {
          await validateToken(token);
        } catch (error) {
          logger.error('🔍 Token validation failed on mount:', error);
          setIsAuthenticated(false);
        }
      } else {
        logger.debug('🔍 No token found on mount');
        setIsAuthenticated(false);
      }
      setInitialized(true);
    };
    
    checkAuth();
  }, []);

  // Add a token validation function
  const validateToken = async (tokenToValidate) => {
    logger.debug('🔍 Starting token validation process with token:', tokenToValidate ? `${tokenToValidate.substring(0, 10)}...` : 'no token');
    const token = tokenToValidate || localStorage.getItem('token');
    if (!token) {
      logger.debug('🔍 No token found for validation');
      return false;
    }

    // First, directly extract userId from token and update localStorage
    try {
      const tokenUserId = extractUserIdFromToken(token);
      if (tokenUserId) {
        const storedUserId = localStorage.getItem('userId');
        if (!storedUserId || storedUserId !== tokenUserId) {
          logger.debug('🔍 Directly updating userId in localStorage from token:', tokenUserId);
          localStorage.setItem('userId', tokenUserId);
        }
      }
    } catch (error) {
      logger.error('Error extracting userId from token:', error);
    }

    // Try to validate token with backend
    try {
      // Try multiple endpoints to validate the token
      let validationSuccessful = false;
      let validationErrors = [];
      
      // First, try to debug the token to see if it's valid
      try {
        logger.debug('🔍 Attempting to debug token structure');
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const debugResponse = await axios.post(`${baseUrl}/api/users/debug-token`, 
          { token },
          { timeout: 5000 }
        );
        
        logger.debug('🔍 Token debug response:', debugResponse.data);
        
        // Check if token is expired according to debug endpoint
        if (debugResponse.data.expired) {
          logger.error('🔍 Token is expired according to debug endpoint');
          throw new Error('Token is expired');
        }
        
        // Check if userId in token matches localStorage
        const storedUserId = localStorage.getItem('userId');
        if (debugResponse.data.userId) {
          // Always ensure userId is stored in localStorage
          if (!storedUserId || debugResponse.data.userId !== storedUserId) {
            logger.warn('🔍 UserId mismatch or missing: token contains', 
              debugResponse.data.userId, 'but localStorage has', storedUserId || 'undefined');
            // Update localStorage with the correct userId from token
            localStorage.setItem('userId', debugResponse.data.userId);
            logger.debug('🔍 Updated localStorage userId to match token');
          } else {
            logger.debug('🔍 UserId in token matches localStorage:', debugResponse.data.userId);
          }
        } else {
          logger.error('🔍 No userId found in token debug response');
        }
      } catch (debugError) {
        logger.warn('🔍 Token debug endpoint failed:', debugError.message);
        // Continue with validation even if debug fails
      }
      
      // Try the user info endpoint first with a shorter timeout
      try {
        logger.debug('🔍 Attempting validation with /api/users/info endpoint');
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const response = await axios.get(`${baseUrl}/api/users/info`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000 // Increased timeout for more reliability
        });
        logger.debug('✅ /api/users/info validation successful, response:', response.status);
        validationSuccessful = true;
        logger.debug('Token validated with /api/users/info endpoint');
      } catch (primaryError) {
        logger.debug('❌ Primary validation endpoint failed:', primaryError.message);
        logger.debug('Status code:', primaryError.response?.status);
        logger.debug('Response data:', primaryError.response?.data);
        validationErrors.push({ endpoint: '/api/users/info', error: primaryError.message });
        
        // If that fails, try the user-specific endpoint as a fallback
        try {
          const userId = localStorage.getItem('userId');
          logger.debug('🔍 Attempting validation with /api/users/{userId} endpoint, userId:', userId);
          if (!userId) throw new Error('No userId available for validation');
          
          const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
          const response = await axios.get(`${baseUrl}/api/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 8000 // Increased timeout
          });
          logger.debug('✅ /api/users/{userId} validation successful, response:', response.status);
          validationSuccessful = true;
          logger.debug('Token validated with /api/users/{userId} endpoint');
        } catch (secondaryError) {
          logger.debug('❌ Secondary validation endpoint failed:', secondaryError.message);
          logger.debug('Status code:', secondaryError.response?.status);
          logger.debug('Response data:', secondaryError.response?.data);
          validationErrors.push({ endpoint: '/api/users/{userId}', error: secondaryError.message });
          
          // If that also fails, try the MVP endpoint
          try {
            logger.debug('🔍 Attempting validation with /api/users/mvp endpoint');
            const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
            const response = await axios.get(`${baseUrl}/api/users/mvp`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 8000 // Increased timeout
            });
            logger.debug('✅ /api/users/mvp validation successful, response:', response.status);
            validationSuccessful = true;
            logger.debug('Token validated with /api/users/mvp endpoint');
          } catch (finalError) {
            logger.debug('❌ Final validation endpoint failed:', finalError.message);
            logger.debug('Status code:', finalError.response?.status);
            logger.debug('Response data:', finalError.response?.data);
            validationErrors.push({ endpoint: '/api/users/mvp', error: finalError.message });
            logger.error('All token validation attempts failed');
          }
        }
      }
      
      if (validationSuccessful) {
        // Explicitly set authentication state to true
        setIsAuthenticated(true);
        
        // Make sure the token is still in localStorage before fetching the profile
        // This ensures the token is available to fetchUserProfile
        if (!localStorage.getItem('token')) {
          logger.debug('🔍 Token was validated but not found in localStorage, restoring it');
          localStorage.setItem('token', token);
        }
        
        // If we have a userId in the token but not in localStorage, restore it
        const decodedToken = parseJwt(token);
        if (decodedToken && decodedToken.userId && !localStorage.getItem('userId')) {
          logger.debug('🔍 UserId found in token but not in localStorage, restoring it');
          localStorage.setItem('userId', decodedToken.userId);
        }
        
        // Fetch user profile after successful validation
        try {
          logger.debug('🔍 Fetching user profile after successful token validation');
          await fetchUserProfile();
          logger.debug('🔍 User profile fetched successfully');
          // Double-check that isAuthenticated is still true after profile fetch
          setIsAuthenticated(true);
        } catch (profileError) {
          logger.error('🔍 Error fetching user profile after token validation:', profileError);
          // Even if profile fetch fails, keep authentication state true if token is valid
          setIsAuthenticated(true);
        }
        
        return true; // Return true to indicate successful validation
      } else {
        // If all validation attempts failed, clear tokens and set unauthenticated
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        setUser(null);
        throw new Error(`Token validation failed on all endpoints: ${JSON.stringify(validationErrors)}`);
      }
    } catch (error) {
      logger.error('Token validation error:', error);
      // Clear tokens and set unauthenticated state
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('Error parsing JWT token:', error);
      return null;
    }
  };

  // Helper function to ensure userId is extracted from token and stored in localStorage
  const ensureUserIdFromToken = (token) => {
    if (!token) {
      logger.error('Cannot extract userId: No token provided');
      return false;
    }
    
    try {
      // Parse the token to extract userId
      const decoded = parseJwt(token);
      
      if (!decoded || !decoded.userId) {
        logger.error('Token does not contain userId:', decoded);
        return false;
      }
      
      const tokenUserId = decoded.userId;
      const storedUserId = localStorage.getItem('userId');
      
      // Always update localStorage with the userId from token
      if (!storedUserId || storedUserId !== tokenUserId) {
        logger.debug(`Updating userId in localStorage: ${tokenUserId}`);
        localStorage.setItem('userId', tokenUserId);
      }
      
      return true;
    } catch (error) {
      logger.error('Error extracting userId from token:', error);
      return false;
    }
  };

  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      logger.debug('👤 UserContext: Fetching user profile with token:', token ? `${token.substring(0, 10)}...` : 'no token');
      logger.debug('👤 UserContext: User ID for profile fetch:', userId);
      
      if (!token) {
        logger.error('👤 UserContext: No token available for profile fetch');
        setIsAuthenticated(false);
        throw new Error('Authentication required');
      }

      if (!userId) {
        logger.warn('👤 UserContext: No userId available, will try to fetch from generic endpoints');
      }

      // Try the primary endpoint
      try {
        logger.debug('👤 UserContext: Trying primary endpoint /api/users/info');
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const response = await axios.get(`${baseUrl}/api/users/info`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 8000 // Increased timeout for more reliability
        });

        if (response.data) {
          logger.debug('👤 UserContext: Primary endpoint successful, user data received');
          setUser(response.data);
          setIsAuthenticated(true);
          logger.debug('👤 UserContext: Authentication state set to true');
          return response; // Return the full response object
        } else {
          logger.warn('👤 UserContext: Primary endpoint returned empty data');
          throw new Error('Empty response from primary endpoint');
        }
      } catch (primaryError) {
        logger.warn('👤 UserContext: Primary user info endpoint failed:', primaryError.message);
        logger.debug('👤 UserContext: Status code:', primaryError.response?.status);
        
        // Enhanced error logging for primary endpoint
        logger.error('👤 UserContext: Primary endpoint detailed error:', {
          endpoint: '/api/users/info',
          status: primaryError.response?.status,
          statusText: primaryError.response?.statusText,
          data: primaryError.response?.data,
          headers: primaryError.response?.headers,
          url: primaryError.config?.url,
          method: primaryError.config?.method,
          timestamp: new Date().toISOString()
        });
        
        // Try user-specific endpoint if userId is available
        if (userId) {
          try {
            logger.debug(`👤 UserContext: Trying user-specific endpoint /api/users/${userId}`);
            const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
            const fallbackResponse = await axios.get(`${baseUrl}/api/users/${userId}`, {
              headers: {
                Authorization: `Bearer ${token}`
              },
              timeout: 8000 // Increased timeout
            });
            
            if (fallbackResponse.data) {
              logger.debug('👤 UserContext: User-specific endpoint successful, user data received');
              setUser(fallbackResponse.data);
              setIsAuthenticated(true);
              logger.debug('👤 UserContext: Authentication state set to true');
              return fallbackResponse; // Return the full response object
            } else {
              logger.warn('👤 UserContext: User-specific endpoint returned empty data');
              throw new Error('Empty response from user-specific endpoint');
            }
          } catch (userEndpointError) {
            logger.warn('👤 UserContext: User-specific endpoint failed:', userEndpointError.message);
            logger.debug('👤 UserContext: Status code:', userEndpointError.response?.status);
            
            // Enhanced error logging for user-specific endpoint
            logger.error('👤 UserContext: User-specific endpoint detailed error:', {
              endpoint: `/api/users/${userId}`,
              status: userEndpointError.response?.status,
              statusText: userEndpointError.response?.statusText,
              data: userEndpointError.response?.data,
              headers: userEndpointError.response?.headers,
              url: userEndpointError.config?.url,
              method: userEndpointError.config?.method,
              timestamp: new Date().toISOString()
            });
            
            // Continue to next fallback
          }
        }
        
        // Try profile endpoint as another fallback
        try {
          logger.debug('👤 UserContext: Trying profile endpoint /api/users/profile');
          const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
          const profileResponse = await axios.get(`${baseUrl}/api/users/profile`, {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 8000
          });
          
          if (profileResponse.data) {
            logger.debug('👤 UserContext: Profile endpoint successful, user data received');
            setUser(profileResponse.data);
            setIsAuthenticated(true);
            logger.debug('👤 UserContext: Authentication state set to true');
            return profileResponse; // Return the full response object
          } else {
            logger.warn('👤 UserContext: Profile endpoint returned empty data');
            throw new Error('Empty response from profile endpoint');
          }
        } catch (profileError) {
          logger.warn('👤 UserContext: Profile endpoint failed:', profileError.message);
          logger.debug('👤 UserContext: Status code:', profileError.response?.status);
          
          // Enhanced error logging for profile endpoint
          logger.error('👤 UserContext: Profile endpoint detailed error:', {
            endpoint: '/api/users/profile',
            status: profileError.response?.status,
            statusText: profileError.response?.statusText,
            data: profileError.response?.data,
            headers: profileError.response?.headers,
            url: profileError.config?.url,
            method: profileError.config?.method,
            timestamp: new Date().toISOString()
          });
          
          // Last resort - try MVP endpoint
          try {
            logger.debug('👤 UserContext: Trying MVP endpoint /api/users/mvp');
            const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
            const mvpResponse = await axios.get(`${baseUrl}/api/users/mvp`, {
              headers: {
                Authorization: `Bearer ${token}`
              },
              timeout: 8000
            });
            
            if (mvpResponse.data && mvpResponse.data.userId) {
              logger.debug('👤 UserContext: MVP endpoint successful, partial user data received');
              // Create a minimal user object from MVP data
              const minimalUser = {
                _id: mvpResponse.data.userId,
                id: mvpResponse.data.userId,
                name: mvpResponse.data.name || 'User',
                email: mvpResponse.data.email || '',
                role: mvpResponse.data.role || 'athlete'
              };
              
              setUser(minimalUser);
              setIsAuthenticated(true);
              logger.debug('👤 UserContext: Authentication state set to true with minimal user data');
              
              // Return a response-like object with the minimal user data
              return { data: minimalUser };
            } else {
              logger.warn('👤 UserContext: MVP endpoint returned insufficient data');
              throw new Error('Insufficient data from MVP endpoint');
            }
          } catch (mvpError) {
            logger.error('👤 UserContext: All profile fetch endpoints failed');
            logger.warn('👤 UserContext: MVP endpoint failed:', mvpError.message);
            logger.debug('👤 UserContext: Status code:', mvpError.response?.status);
            
            // Enhanced error logging for MVP endpoint
            logger.error('👤 UserContext: MVP endpoint detailed error:', {
              endpoint: '/api/users/mvp',
              status: mvpError.response?.status,
              statusText: mvpError.response?.statusText,
              data: mvpError.response?.data,
              headers: mvpError.response?.headers,
              url: mvpError.config?.url,
              method: mvpError.config?.method,
              timestamp: new Date().toISOString()
            });
            
            // All endpoints failed, create a minimal placeholder user
            const localUserId = localStorage.getItem('userId');
            // Create a minimal placeholder user to prevent UI issues
            setUser({
              _id: localUserId || 'unknown',
              id: localUserId || 'unknown',
              name: 'User',
              email: '',
              isPlaceholder: true // Flag to indicate this is not a real user profile
            });
          }
        }
      }
    } catch (err) {
      logger.error('👤 UserContext: Error fetching user profile:', err.message);
      
      // Handle 401 errors specifically
      if (err.response && err.response.status === 401) {
        logger.debug('👤 UserContext: Authentication token expired or invalid');
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        setUser(null);
        setError('Your session has expired. Please log in again.');
      } else if (err.code === 'ECONNABORTED') {
        // Handle timeout errors
        logger.debug('👤 UserContext: Connection timed out. Using cached user data if available.');
        // Don't clear auth state on timeout - just show an error
        setError('Connection to server timed out. Some features may be limited.');
        
        // If we have a user already, keep them authenticated
        if (!user) {
          const localUserId = localStorage.getItem('userId');
          // Create a minimal placeholder user to prevent UI issues
          setUser({
            _id: localUserId || 'unknown',
            id: localUserId || 'unknown',
            name: 'User',
            email: '',
            isPlaceholder: true // Flag to indicate this is not a real user profile
          });
        }
      } else {
        // For other errors, keep the user authenticated if we have a token
        // but show an error message
        const localToken = localStorage.getItem('token');
        const localUserId = localStorage.getItem('userId');
        
        if (localToken) {
          setError('Failed to fetch complete user profile. Some features may be limited.');
          
          // If we don't have a user object yet, create a minimal one
          if (!user) {
            setUser({
              _id: localUserId || 'unknown',
              id: localUserId || 'unknown',
              name: 'User',
              email: '',
              isPlaceholder: true
            });
          }
        } else {
          setError('Failed to fetch user profile');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      // Clear any existing tokens first to prevent token persistence issues
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      logger.debug('🔑 UserContext: Attempting login with credentials');
      const response = await axios.post(
        `${API_URL}/api/users/login`,
        credentials
      );

      const { token, userId } = response.data;
      
      if (!token || !userId) {
        throw new Error('Invalid response from server: missing token or userId');
      }
      
      logger.debug('🔑 UserContext: Login successful, received token and userId');
      
      // Store token and userId
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      
      // Set authenticated state
      setIsAuthenticated(true);
      
      // Validate token immediately to ensure it works
      try {
        logger.debug('🔑 UserContext: Validating token after login');
        await validateToken(token);
        logger.debug('🔑 UserContext: Token validation successful');
      } catch (validationError) {
        logger.error('🔑 UserContext: Token validation failed:', validationError);
        // If token validation fails, clear tokens and throw error
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        setIsAuthenticated(false);
        throw new Error('Token validation failed after login');
      }
      
      // Fetch user profile after successful token validation
      logger.debug('🔑 UserContext: Fetching user profile');
      await fetchUserProfile();
      
      // Track login with Mixpanel
      Mixpanel.identify(userId);
      
      // Update user profile properties in Mixpanel
      const userProfile = await fetchUserData(token);
      if (userProfile) {
        Mixpanel.setUserProfile({
          $email: userProfile.email,
          $name: userProfile.name,
          $last_login: new Date().toISOString(),
          sport: userProfile.sport,
          position: userProfile.position,
          subscription_status: userProfile.subscriptionStatus || 'free'
        });
      }
      
      Mixpanel.track('User Login', {
        loginMethod: 'email',
        timestamp: new Date().toISOString()
      });
      
      return { success: true };
    } catch (err) {
      logger.error('Login error:', err);
      // Clear any tokens that might have been set
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setIsAuthenticated(false);
      setError(err.response?.data?.message || err.message || 'Failed to login');
      return { success: false, error: err.response?.data?.message || err.message || 'Failed to login' };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      // Clear any existing tokens before registration to prevent conflicts
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      logger.debug('Starting registration process...', { email: userData.email });
      const response = await axios.post(
        `${API_URL}/api/users/register`,
        userData,
        { timeout: 10000 } // Increase timeout for registration requests
      );

      logger.debug('Registration response received:', {
        status: response.status,
        hasToken: !!response.data?.token,
        hasId: !!response.data?._id,
        responseData: JSON.stringify(response.data)
      });
      
      // Handle the backend response format - it returns _id instead of userId
      if (!response.data || !response.data.token || !response.data._id) {
        logger.error('Invalid registration response:', response.data);
        throw new Error('Server returned invalid registration data');
      }
      
      const { token, _id: userId } = response.data;
      
      // Verify token format before saving
      try {
        // Make a request to debug the token before saving it
        const debugResponse = await axios.post(
          `${API_URL}/api/users/debug-token`,
          { token },
          { timeout: 5000 }
        );
        logger.debug('Token debug info:', debugResponse.data);
        
        // Verify userId consistency
        if (debugResponse.data?.decoded?.userId !== userId) {
          logger.warn('UserId mismatch!', {
            tokenUserId: debugResponse.data?.decoded?.userId,
            responseUserId: userId
          });
          // Use the userId from the token as it will be used for authentication
          localStorage.setItem('userId', debugResponse.data?.decoded?.userId);
        } else {
          localStorage.setItem('userId', userId);
        }
      } catch (tokenError) {
        logger.warn('Token debug failed, proceeding anyway:', tokenError);
        localStorage.setItem('userId', userId);
      }
      
      // Store token
      localStorage.setItem('token', token);
      
      // Verify storage was successful
      const savedToken = localStorage.getItem('token');
      const savedUserId = localStorage.getItem('userId');
      
      logger.debug('Storage verification:', {
        tokenSaved: !!savedToken,
        userIdSaved: !!savedUserId,
        savedUserId
      });
      
      if (savedToken && savedUserId) {
        // Mark as new user to help other components optimize
        localStorage.setItem('isNewUser', 'true');
        localStorage.setItem('onboardingNeeded', 'true');
        
        // Set default onboarding content to prevent unnecessary API calls
        const defaultOnboardingContent = {
          introduction: [
            "Welcome to Mental Edge Coach! We're excited to help you improve your mental performance.",
            "This app is designed to help athletes like you develop mental skills that translate to better performance.",
            "Let's get started by understanding more about you and your goals."
          ]
        };
        localStorage.setItem('onboardingContent', JSON.stringify(defaultOnboardingContent));
        
        // Set authentication state
        setIsAuthenticated(true);
        
        // Set current time as last login time
        localStorage.setItem('lastLoginTime', new Date().getTime());
        
        logger.debug('Authentication data saved successfully');
        
        // Track registration event with Mixpanel
        Mixpanel.identify(savedUserId);
        Mixpanel.setUserProfile({
          $email: userData.email,
          $name: userData.name,
          $created: new Date().toISOString(),
          isNewUser: true
        });
        Mixpanel.track('User Registration', {
          registrationMethod: 'email',
          timestamp: new Date().toISOString()
        });
        
        // Validate token immediately to ensure it works
        try {
          logger.debug('Validating token after registration...');
          const isValid = await validateToken(savedToken);
          logger.debug('Token validation result:', { isValid });
          
          if (!isValid) {
            logger.warn('Token validation failed after registration');
            // Continue anyway as the token might still work later
          }
        } catch (validationError) {
          logger.error('Error validating token after registration:', validationError);
          // Continue anyway since we already have authentication
        }
        
        // Fetch user profile after a slight delay to ensure token is properly saved
        setTimeout(async () => {
          try {
            await fetchUserProfile();
          } catch (profileError) {
            logger.error('Error fetching initial profile after registration:', profileError);
            // Continue anyway since we already have authentication
          }
        }, 1000); // Increased delay to give backend more time
        
        return { success: true };
      } else {
        throw new Error('Failed to save authentication data to localStorage');
      }
    } catch (err) {
      logger.error('Registration error:', err);
      setError(err.response?.data?.message || 'Failed to register');
      setIsAuthenticated(false);
      
      // Clean up any partial data
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('onboardingNeeded');
      
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'Failed to register' 
      };
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (token) => {
    try {
      // Try primary endpoint
      try {
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const response = await axios.get(`${baseUrl}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
      } catch (primaryError) {
        logger.warn('Primary profile endpoint failed for Mixpanel data:', primaryError);
        
        // Try fallback endpoint
        const userId = localStorage.getItem('userId');
        if (!userId) {
          throw new Error('User ID not available for fallback endpoint');
        }
        
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const fallbackResponse = await axios.get(`${baseUrl}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return fallbackResponse.data;
      }
    } catch (error) {
      logger.error('Error fetching user data for Mixpanel:', error);
      return null;
    }
  };

  const logout = async () => {
    logger.debug('🔒 UserContext: Logout initiated');
    try {
      // First, try to terminate the session on the backend
      const token = localStorage.getItem('token');
      if (token) {
        logger.debug('🔒 UserContext: Attempting to terminate backend session');
        try {
          const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
          const response = await axios.post(
            `${baseUrl}/api/users/logout`, 
            {}, 
            {
              headers: { 
                Authorization: `Bearer ${token}` 
              },
              timeout: 5000 // Add a timeout to prevent hanging
            }
          );
          logger.debug('🔒 UserContext: Backend session terminated successfully', response.status);
        } catch (error) {
          logger.error('🔒 UserContext: Failed to terminate backend session:', error.message);
          // Continue with local logout even if backend call fails
        }
      }
    } catch (error) {
      logger.error('🔒 UserContext: Error during logout process:', error);
    } finally {
      // Always perform these cleanup steps regardless of backend API success/failure
      
      logger.debug('🔒 UserContext: Clearing localStorage items');
      // Clear all auth-related items from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      logger.debug('🔒 UserContext: Clearing session storage');
      // Clear session storage as well
      sessionStorage.clear();
      
      logger.debug('🔒 UserContext: Clearing cookies');
      // Clear cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      logger.debug('🔒 UserContext: Resetting state');
      // Reset state
      setUser(null);
      setIsAuthenticated(false);
      
      // Reset Mixpanel user on logout
      Mixpanel.reset();
      
      logger.debug('🔒 UserContext: Logout completed');
    }
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      // Try multiple approaches to update the profile
      let response;
      let updateSuccessful = false;
      let updateError = null;
      
      // First try the standard endpoint
      try {
        logger.debug('UserContext: Attempting profile update with standard endpoint');
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        response = await axios.put(
          `${baseUrl}/api/users/profile`,
          profileData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        updateSuccessful = true;
      } catch (primaryError) {
        logger.warn('UserContext: Primary profile update endpoint failed:', primaryError);
        updateError = primaryError;
        
        // Try the user-specific endpoint as fallback
        try {
          logger.debug('UserContext: Attempting profile update with user-specific endpoint');
          if (!userId) {
            throw new Error('User ID not available for fallback endpoint');
          }
          
          const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
          response = await axios.put(
            `${baseUrl}/api/users/${userId}`,
            profileData,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          updateSuccessful = true;
        } catch (fallbackError) {
          logger.error('UserContext: Fallback profile update endpoint failed:', fallbackError);
          updateError = fallbackError;
          
          // Try a direct POST to info endpoint as last resort
          try {
            logger.debug('UserContext: Attempting profile update with info endpoint');
            const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
            response = await axios.post(
              `${baseUrl}/api/users/info`,
              { 
                ...profileData, 
                userId: userId,
                updateType: 'profile'
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            updateSuccessful = true;
          } catch (lastError) {
            logger.error('UserContext: All profile update endpoints failed:', lastError);
            updateError = lastError;
          }
        }
      }
      
      if (!updateSuccessful) {
        throw updateError || new Error('Failed to update profile after trying all available endpoints');
      }
      
      // Update local user state
      setUser(prevUser => ({
        ...prevUser,
        ...response.data
      }));
      
      // Update Mixpanel user profile with the latest data
      Mixpanel.setUserProfile({
        $name: profileData.name || user.name,
        sport: profileData.sport || user.sport,
        position: profileData.position || user.position,
        birthYear: profileData.birthYear || user.birthYear,
        lastUpdated: new Date().toISOString()
      });
      
      // Track profile update event
      Mixpanel.track('Profile Updated', {
        updatedFields: Object.keys(profileData),
        timestamp: new Date().toISOString()
      });
      
      return { success: true };
    } catch (err) {
      logger.error('Profile update error:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
      return { success: false, error: err.response?.data?.message || 'Failed to update profile' };
    } finally {
      setLoading(false);
    }
  };

  // Value object that will be passed to consumers
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    setIsAuthenticated, // Add this to expose the function to consumers
    login,
    register,
    logout,
    updateProfile,
    fetchUserProfile,
    validateToken
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
